import { Code, ConnectError } from '@bufbuild/connect';
import { useEffect } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router';

import api from '@/shared/api/api';
import { PATHS } from '@/shared/config';
import { useAppDispatch } from '@/shared/hooks';
import { useHubspotEvents } from '@/shared/hooks/useHubspotEvents';
import {
  isConnectNetworkError,
  isDemoSession,
  setFaroUser,
} from '@/shared/lib';
import { debug } from '@/shared/lib/debug';

import { authModel } from '@/entities/auth';
import { organizationModel } from '@/entities/organization';
import { userModel } from '@/entities/user';

import { useSyncOrganizationID } from './useSyncOrganizationID';

let abortController: AbortController;

export const useUserDataStream = (invitationToken: string | undefined) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { setIdentity, trackPage } = useHubspotEvents();

  const navigateToSignInFromCurrentLocation = () => {
    navigate(PATHS.signIn, {
      state: { from: location?.pathname + location?.search },
    });
  };

  const openUserDataStream = async () => {
    abortController = new AbortController();

    dispatch(organizationModel.actions.setLoading('pending'));
    dispatch(userModel.actions.setLoading('pending'));

    try {
      const userDataStream = api.user.myDataStream(
        {},
        { signal: abortController.signal },
      );

      for await (const { Update } of userDataStream) {
        switch (Update.case) {
          case 'HistoricalMe': {
            if (!isDemoSession()) {
              setIdentity({
                email: Update.value.PersonalData?.Emails[0] ?? '',
                options: {
                  id: Update.value.ID,
                },
              });
            }
            trackPage(window.location.origin + window.location.pathname);
            setFaroUser(Update.value.ID);

            dispatch(
              userModel.thunks.setUserLocaleWithStorage(
                Update.value?.Settings?.Language,
              ),
            );

            dispatch(userModel.actions.addUser(Update.value));
            dispatch(userModel.actions.setLoading('succeeded'));
            break;
          }

          case 'UpdatedMe': {
            dispatch(
              userModel.thunks.setUserLocaleWithStorage(
                Update.value?.Settings?.Language,
              ),
            );

            dispatch(userModel.actions.addUser(Update.value));
            break;
          }

          case 'HistoricalOrganizations': {
            dispatch(
              organizationModel.actions.setNewestMany(
                Update.value.Organizations,
              ),
            );
            // There is no EndOfHistoricalOrganizations in the API
            dispatch(organizationModel.actions.setLoading('succeeded'));
            break;
          }

          case 'UpdatedOrganization': {
            dispatch(organizationModel.actions.setNewestOne(Update.value));
            break;
          }
        }
      }
    } catch (error) {
      const connectError = ConnectError.from(error);

      if (isConnectNetworkError(connectError)) {
        debug('Network error detected, attempting to restart stream...');
        closeUserDataStream();
        openUserDataStream();
        return;
      }

      if (connectError.code === Code.Canceled) {
        return;
      }

      console.error(
        'MyDataStream:: error: ',
        connectError.message,
        connectError.code,
        connectError.rawMessage,
        connectError,
      );

      // TODO: [2/m] think about how to improve navigation
      const shouldNavigateToSignIn =
        connectError.code === Code.Unauthenticated ||
        (connectError.code === Code.Unimplemented &&
          connectError.rawMessage.includes('HTTP 404'));

      if (shouldNavigateToSignIn) {
        // Reset to idle so the next sign-in attempt shows spinner instead of
        // immediately redirecting back (failed state would skip the spinner).
        dispatch(userModel.actions.setLoading('idle'));
        dispatch(organizationModel.actions.setLoading('idle'));
        if (invitationToken) {
          try {
            const { IsAlreadyRegistered } = await dispatch(
              authModel.thunks.checkInviteToken({ Token: invitationToken }),
            ).unwrap();

            if (IsAlreadyRegistered) {
              navigate(PATHS.signIn, {
                state: {
                  from: generatePath(PATHS.organization_invite, {
                    token: invitationToken,
                  }),
                },
              });
            } else {
              navigate(PATHS.signUp, { state: { invitationToken } });
            }
          } catch (checkInviteTokenError) {
            // An expired or truncated token must still land somewhere: an
            // unhandled rejection here leaves the app on the loading spinner
            // with no user and no navigation.
            debug(
              'Failed to check the invitation token',
              checkInviteTokenError,
            );

            navigateToSignInFromCurrentLocation();
          }
        } else {
          navigateToSignInFromCurrentLocation();
        }
      } else {
        dispatch(userModel.actions.setLoading('failed'));
        dispatch(organizationModel.actions.setLoading('failed'));
      }
    }
  };

  const closeUserDataStream = () => {
    if (abortController) {
      abortController.abort();
    }
  };

  // verify available current organization and sync with localStorage
  useSyncOrganizationID();

  useEffect(() => {
    openUserDataStream();

    return () => {
      closeUserDataStream();
    };
  }, []);
};
